<!--suppress CssUnusedSymbol -->
<template>
  <main id="main-content" class="flex-display-body container py-5">
    <page-loading-wrapper :is-loading="fetchingGroups">
      <template>
        <div class="row">
          <div class="col-12">
            <template v-if="administeredOrManagedGroups && administeredOrManagedGroups.length > 0">
              <h1 class="display-4 pb-3">Manage by Member</h1>
              <form @submit.prevent="checkForm">
                <div class="form-group">
                  <label for="searchMember" class="lead">
                    Enter a PID to access that user's membership status in groups you manage.
                  </label>
                  <div class="input-group">
                    <input id="searchMember"
                           type="search"
                           maxlength="50"
                           placeholder="Enter a PID"
                           class="form-control"
                           v-model.trim="searchTerm"/>
                    <div class="input-group-append">
                      <button class="btn btn-primary" type="submit" aria-label="Search">
                        <font-awesome-icon icon="search"></font-awesome-icon>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
              <div class="message-row">
                <template v-if="searching">
                  <p>
                    <font-awesome-icon icon="spinner" class="fa-spin"></font-awesome-icon>
                    <b>Searching...</b>
                  </p>
                </template>
                <!-- Display errors for the PID search form. -->
                <template v-if="errors.length">
                  <ul>
                    <li v-for="error in errors" :key="error">{{ error }}</li>
                  </ul>
                </template>
                <!-- Display errors for batch add/remove operations. -->
                <b-alert id="alert-group-batch-add-member-success"
                         v-if="batchUpdateResponse.success.length > 0 || batchUpdateResponse.conflicts.length > 0"
                         variant="success" dismissible show fade
                >
                  <span v-html="batchUpdateResponse.batchType === 'ADD' ?
                   successfulBatchAddMessage : successfulBatchRemoveMessage">
                  </span>
                </b-alert>
                <b-alert id="alert-group-batch-add-member-errors" v-if="batchUpdateResponse.errors.length > 0"
                         variant="danger" dismissible show fade
                >
                  <span class="bold">The following errors occurred:</span>
                  <div class="scroll-container">
                    <ul v-for="(error) in batchUpdateResponse.errors" :key="error.instance">
                      <li>{{error.instance}} - {{error.code}} : {{error.message}}</li>
                    </ul>
                  </div>
                </b-alert>
              </div>
              <!-- Show options once a valid pid has been found -->
              <transition name="fade" mode="out-in">
                <div v-if="searchedPid">
                  <hr>
                  <p class="lead">Results for PID
                    <span class="font-weight-bold burnt-orange-web-text">{{ searchedPid }}</span>:
                  </p>
                  <!-- Options for batch add/remove -->
                  <b-dropdown :disabled="batchUpdateForm.submitting" right text="Batch Update" variant="outline-primary"
                              size="sm" offset="50" class="mb-2">
                    <b-dropdown-item size="sm" @click="setBatchUpdateForType('ADD')">Add</b-dropdown-item>
                    <b-dropdown-item size="sm" @click="setBatchUpdateForType('REMOVE')">Remove</b-dropdown-item>
                  </b-dropdown>
                  <!-- Show batch add/remove form. -->
                  <template v-if="batchUpdateForm.isBatchAdd || batchUpdateForm.isBatchRemove">
                    <form @submit.prevent="batchUpdate(batchUpdateForm.isBatchAdd ? 'ADD' : 'REMOVE')">
                      <hr>
                      <div class="form-group">
                        <div v-if="batchUpdateForm.isBatchAdd">
                          Groups to add <b>{{searchedPid}}</b> to:
                        </div>
                        <div v-else>
                          Groups to remove <b>{{searchedPid}}</b> from:
                        </div>
                        <textarea id="batch-update-member-for-groups-field"
                                  class="form-control"
                                  rows="3"
                                  v-model="batchUpdateForm.groupsToUpdate">
                        </textarea>
                        <small id="batch-update-member-for-groups-help"
                               class="form-text text-muted text-break-word">
                          <span v-html="batchUpdateSeparatorMsg" />
                        </small>
                        <small>
                          <span v-html="batchUpdateTimeoutWarning" />
                        </small>
                      </div>
                      <b-btn id="btn-batch-update-member-for-groups-submit"
                             type="submit"
                             variant="outline-primary"
                             :disabled="batchUpdateForm.submitting || batchUpdateForm.groupsToUpdate.length < 3">
                        {{batchUpdateForm.submitting === true ? 'Submitting...' : 'Update Membership'}}
                      </b-btn>
                      <b-btn id="btn-batch-update-member-for-groups-cancel"
                             type="reset"
                             variant="outline-secondary"
                             :disabled="batchUpdateForm.submitting"
                             @click="batchCancel">
                        Cancel
                      </b-btn>
                    </form>
                  </template>
                  <!-- Show table for add/remove member to/from a single group. -->
                  <template v-else>
                    <table-options-filter table-id="tbl-group-membership"
                                          :per-page="tablePerPage"
                                          @perPageChanged="onPerPageChanged"
                                          @filterChanged="onFilterChanged">
                    </table-options-filter>
                    <b-table show-empty
                             striped
                             hover
                             stacked="md"
                             id="tbl-group-membership"
                             :no-sort-reset="true"
                             sort-by="membershipStatus"
                             :sort-desc="true"
                             :items="computedGroups"
                             :fields="fields"
                             :sort-compare="nestedSortCompare"
                             :current-page="currentPage"
                             :per-page="tablePerPage"
                             :filter="tableFilter"
                             @filtered="onFiltered">
                      <template slot="table-caption">
                        {{searchedPid}}'s membership status
                      </template>
                      <template slot="managedGroup.uugid" slot-scope="row">
                        <b-link
                          :to="{
                               name: 'GroupDashboard',
                               params: {id: row.item.key},
                               selectedComponent: 'appGroupDetail'
                             }"
                          class="break-words"
                        >
                          {{row.item.key}}
                        </b-link>
                      </template>
                      <template slot="membershipStatus" slot-scope="row">
                        <!-- Using a disabled button to indicate if the update failed for this group since size and
                        position will be exactly equivalent to the actual submit button. This prevents having to
                        retrieve dimensions and styling info from the button to create a div. There may be a better way
                         to do this and can be updated. -->
                        <button :id="'btn-indicator-' + row.item.key"
                                class="full-width btn btn-sm btn-danger"
                                :disabled="true"
                                style="display: none;">
                          Update Failed
                        </button>
                        <button :id="'btn-update-' + row.item.key"
                                class="full-width btn btn-sm"
                                @click.stop="modifyMemberForGroup(row.item, $event)"
                                :class="isMember(row.item) ? 'btn-primary' : 'btn-secondary'">
                          {{ isMember(row.item) ? `Remove ${searchedPid}` : `Add ${searchedPid}` }}
                        </button>
                      </template>
                    </b-table>
                    <b-row>
                      <b-col md="6" class="my-1">
                        <b-pagination id="mnu-pagination" :total-rows="totalRows" :per-page="tablePerPage"
                                      v-model="currentPage" class="my-0">
                        </b-pagination>
                      </b-col>
                    </b-row>
                  </template>
                </div>
              </transition>
            </template>
            <template v-else>
              <no-administered-groups-alert :include-manager="true"></no-administered-groups-alert>
            </template>
          </div>
        </div>
      </template>
    </page-loading-wrapper>
  </main>
</template>

<script>
/* eslint-disable no-param-reassign */
/* noinspection CssUnusedSymbol */
import { groupsMixin, groupsFetch, util } from '../util/groups-mixin';
import tableMixin from '../util/table-mixin';
import NoAdministeredGroupsAlert from './NoAdministeredGroupsAlert';
import PageLoadingWrapper from './slots/PageLoadingWrapper';
import TableOptionsFilter from './table/TableOptionsFilter';

export default {
  title: 'Manage Member',
  mixins: [groupsMixin, groupsFetch, util, tableMixin],
  data() {
    return {
      fields: [
        {
          key: 'managedGroup.uugid',
          label: 'Group',
          sortable: true,
          tdClass: 'search-group-col',
          thClass: 'tbl-header-uugid',
        },
        {
          key: 'membershipStatus',
          label: 'Modify Membership',
          sortable: true,
          tdClass: 'search-action-col',
          thClass: 'tbl-header-status',
        },
      ],
      searchTerm: null,
      searchedPid: '',
      errors: [],
      searching: false,
      batchUpdateForm: {
        isBatchAdd: false,
        isBatchRemove: false,
        groupsToUpdate: '',
        submitting: false,
      },
      batchUpdateSeparatorMsg: 'The following delimiters will work: spaces, commas, semicolons, new lines',
      batchUpdateTimeoutWarning:
        '<b>Note:</b> Updating a member on numerous groups could cause a frontend timeout. ' +
        'However, the transaction was likely successful. Wait a few minutes and refresh to verify.',
      batchUpdateResponse: {
        batchType: '',
        success: [],
        conflicts: [],
        errors: [],
      },
      // Override the default userRoles provided by groupsMixin#groupsFetch.
      userRoles: ['administrator', 'manager'],
    };
  },
  methods: {
    isMember(group) {
      return this.$store.getters.isMember(group.managedGroup, this.searchedPid);
    },
    /* Add a specific person user to multiple groups in a single transaction. */
    batchUpdate(batchType) {
      this.resetAllFormResponses();
      this.batchUpdateForm.submitting = true;
      this.$store.dispatch('batchUpdateMemberForGroups',
        {
          groupIds: this.groupsStringToArray,
          userId: this.searchedPid,
          modify: batchType,
          type: 'members',
          kind: 'person',
        })
        .then((resp) => {
          const response = resp.data;
          if (batchType === 'ADD') {
            this.setBatchAddResponse(response);
          } else {
            this.setBatchRemoveResponse(response);
          }
          if (this.batchUpdateResponse.success.length > 0) {
            this.$store.commit('clearGroups');
            return this.fetchGroups();
          }
        })
        .finally(() => {
          this.resetBatchUpdateForm();
          this.batchCancel();
        });
    },
    /* Add member to single group. */
    addUserToGroup(group, event) {
      const vm = this;
      this.$store.dispatch('addMemberPerson', { uugid: group.key, roleId: this.searchedPid })
        .then(() => {
          this.$store.dispatch('fetchGroupDetailsFromEd', group.key).then(() => {
            event.target.disabled = false;
          });
        })
        .catch(() => {
          vm.handleFail(event.target, group.key);
        })
        .finally(() => {
          event.target.disabled = false;
        });
    },
    /* Remove member from single group. */
    removeUserFromGroup(group, event) {
      const vm = this;
      this.$store.dispatch('removeGroupRoleUser',
        { uugid: group.key, type: 'members', roleId: this.searchedPid, kind: 'person' },
      )
        .then(() => {
          this.$store.dispatch('fetchGroupDetailsFromEd', group.key).then(() => {
            event.target.disabled = false;
          });
        })
        .catch(() => {
          vm.handleFail(event.target, group.key);
        })
        .finally(() => {
          event.target.disabled = false;
        });
    },
    /* Add/Remove member from single group. */
    modifyMemberForGroup(group, event) {
      event.target.disabled = true;
      if (this.isMember(group)) {
        this.removeUserFromGroup(group, event);
      } else {
        this.addUserToGroup(group, event);
      }
    },
    /* Exit batch update. */
    batchCancel() {
      this.batchUpdateForm.isBatchAdd = false;
      this.batchUpdateForm.isBatchRemove = false;
    },
    setBatchUpdateForType(type) {
      this.resetAllFormResponses();
      this.resetBatchUpdateForm();
      this.batchUpdateForm.isBatchRemove = type !== 'ADD';
      this.batchUpdateForm.isBatchAdd = type !== 'REMOVE';
    },
    resetBatchUpdateResponse() {
      this.batchUpdateResponse.batchType = '';
      this.batchUpdateResponse.success = [];
      this.batchUpdateResponse.conflicts = [];
      this.batchUpdateResponse.errors = [];
    },
    resetBatchUpdateForm() {
      this.batchUpdateForm.groupsToUpdate = '';
      this.batchUpdateForm.submitting = false;
    },
    resetAllFormResponses() {
      this.errors = [];
      this.resetBatchUpdateResponse();
    },
    /* Update the batchUpdateResponse with the response from the server for batch REMOVE operations. */
    setBatchRemoveResponse(response) {
      this.batchUpdateResponse.batchType = 'REMOVE';
      const formattedErrors = [];
      this.batchUpdateResponse.success = response.successfulUpdates;
      response.errors.forEach((error) => {
        // 404 can indicate either that the group does not exist OR the person is not currently a member of the group.
        if (error.code === 404) {
          this.batchUpdateResponse.conflicts.push(error.instance);
        } else {
          formattedErrors.push(error);
        }
      });
      this.batchUpdateResponse.errors = formattedErrors;
    },
    /* Update the batchUpdateResponse with the response from the server for batch ADD operations. */
    setBatchAddResponse(response) {
      this.batchUpdateResponse.batchType = 'ADD';
      const formattedErrors = [];
      this.batchUpdateResponse.success = response.successfulUpdates;
      response.errors.forEach((error) => {
        // 409:Conflict indicates that the person is already a member of the given group.
        if (error.code === 409) {
          this.batchUpdateResponse.conflicts.push(error.instance);
        } else {
          formattedErrors.push(error);
        }
      });
      this.batchUpdateResponse.errors = formattedErrors;
    },
    handleFail(subButton, uugid) {
      const msgContainer = document.getElementById(`btn-indicator-${uugid}`);
      subButton.style.display = 'none';
      msgContainer.style.display = 'block';
      /* Scroll to the top of the page so the user can see the error message */
      window.scrollTo(0, 0);
      this.showStatus(subButton, msgContainer, 7000);
    },
    showStatus(subButton, messageContainer, timeout) {
      setTimeout(() => {
        messageContainer.style.display = 'none';
        subButton.style.display = 'block';
      }, timeout);
    },
    nestedSortCompare(grpOne, grpTwo, key) {
      switch (key) {
        case 'managedGroup.uugid':
          return grpOne.managedGroup.uugid
            .localeCompare(grpTwo.managedGroup.uugid, undefined, {
              numeric: true,
            });
        case 'membershipStatus': {
          const isMemberOfOne = this.isMember(grpOne);
          const isMemberOfTwo = this.isMember(grpTwo);
          if (isMemberOfOne < isMemberOfTwo) {
            return -1;
          } else if (isMemberOfOne > isMemberOfTwo) {
            return 1;
          }
          return 0;
        }
        default:
          return null;
      }
    },
    checkForm() {
      this.errors = [];
      if (!this.searchTerm) {
        this.errors.push('Please enter a PID to search.');
      } else if (this.searchTerm.length < 3) {
        this.errors.push('PID must have at least 3 characters.');
      } else {
        this.searching = true;
        this.$store.dispatch('personExists', this.searchTerm.toLowerCase())
          .then((resp) => {
            if (resp === false) {
              this.errors.push('This PID doesn\'t exist.');
            } else {
              this.searchedPid = this.searchTerm.toLowerCase();
              this.searchTerm = '';
            }
          }).finally(() => {
            this.searching = false;
          });
      }

      if (!this.errors.length) {
        return true;
      }
    },
  },
  computed: {
    computedGroups() {
      let computedResult = this.administeredOrManagedGroups;
      if (this.tableFilter) {
        computedResult = computedResult
          .filter(item => item.managedGroup.uugid.includes(this.tableFilter.toLowerCase()));
        return computedResult;
      }
      return computedResult;
    },
    administeredOrManagedGroups() {
      return this.$store.getters.administeredOrManagedGroups;
    },
    /* Convert the batch add/remove string to an array */
    groupsStringToArray() {
      return this.arrayFromDelimitedString(this.batchUpdateForm.groupsToUpdate);
    },
    successfulBatchAddMessage() {
      let successfulAddMsg = `Successfully added <b>${this.searchedPid}</b>
        as a member of ${this.batchUpdateResponse.success.length} groups.`;
      if (this.batchUpdateResponse.conflicts.length > 0) {
        successfulAddMsg +=
          `<br><b>${this.searchedPid}</b>
            was already a member of ${this.batchUpdateResponse.conflicts.length} group(s).`;
      }
      return successfulAddMsg;
    },
    successfulBatchRemoveMessage() {
      let successfulRemoveMsg = `Successfully removed <b>${this.searchedPid}'s</b> membership from
        ${this.batchUpdateResponse.success.length} groups.`;
      if (this.batchUpdateResponse.conflicts.length > 0) {
        successfulRemoveMsg +=
          `<br>The following groups either don't exist or <b>${this.searchedPid}</b> is not currently a member:
            <b>[ ${this.batchUpdateResponse.conflicts.join(', ')} ]</b>.`;
      }
      return successfulRemoveMsg;
    },
  },
  components: {
    NoAdministeredGroupsAlert,
    PageLoadingWrapper,
    TableOptionsFilter,
  },
};
</script>

<style>
  @media (min-width: 768px) {
    .search-group-col {
      width: 65%;
    }
    .search-expiration-col {
      width: 35%;
    }
  }

  .full-width {
    min-width: 100%;
  }

  .search-group-col {
    word-break: break-all;
  }

  .message-row {
    min-height: 48px;
  }

  .message-row ul {
    color: red;
    font-weight: bold;
    list-style: none;
    padding-left: 0;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .3s ease;
  }

  .fade-enter {
    opacity: 0;
  }

  .fade-leave-to {
    transition: opacity .5s ease;
    opacity: 0;
  }
</style>



// WEBPACK FOOTER //
// src/components/ManageMember.vue